/**
 *
 * CourseDetailMobile
 *
 */

import React, { memo, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Helmet } from 'react-helmet';
import { createStructuredSelector } from 'reselect';
import { compose } from 'redux';
import { useParams } from 'react-router-dom';
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";

// styles
import './style.scss';

import { useInjectSaga } from 'utils/injectSaga';
import { useInjectReducer } from 'utils/injectReducer';
import reducer from './reducer';
import saga from './saga';

import { flushState, loadCourse, updateCourse } from './actions';
import {
  makeSelectLoading,
  makeSelectError,
  makeSelectCourseDetail,
  // on date 
  makeSelectOnDateAbsences,
  makeSelectOnDateAttendances,
} from './selectors';

// antd component
import { Layout } from 'antd';
const { Content } = Layout;

// assets
import { ArrowLeftOutlined } from '@ant-design/icons';

// components
import CourseCard from 'mobile/components/CourseCard';
import ExcusedStudents from 'mobile/components/ExcusedStudents';
// containers
import AttendanceControl from "mobile/pages/AttendanceControl";

// global user
import { withUser } from 'engine/Contexts/User.context';
import { getObjId } from "utils/helpers/helper.general";
import { convertDateToString } from "utils/moment/moment.utils";

export function CourseDetailMobile({
  // main data
  courseDetail,
  onDateAbsences,
  onDateAttendances,
  // states
  user,
  history,
  error,
  loading,
  // actions
  loadCourse,
  updateCourse,
  flushState,
}) {
  useInjectReducer({ key: 'courseDetailMobile', reducer });
  useInjectSaga({ key: 'courseDetailMobile', saga });

  const { t } = useTranslation();

  const { id: initId } = useParams();
  const location = useLocation();

  const [currentCourse, setCurrentCourse] = useState(courseDetail);
  const [currentAttendances, setCurrentAttendances] = useState(onDateAttendances || []);

  const onDate = location?.state?.onDate;
  const timeZone = location?.state?.timeZone;
  const goBack = location?.state?.goBack;
  const goBackTitle = location?.state?.goBackTitle;

  // Get Course Data
  useEffect(() => {
    initId && loadCourse(initId, onDate, timeZone);
  }, [initId]);

  useEffect(() => {
    courseDetail && setCurrentCourse(courseDetail);
    setCurrentAttendances(onDateAttendances || []);
  }, [courseDetail]);

  // Clearing the state after unmounting a component
  // - work like ComponentDidUpdate
  useEffect(() => {
    return () => {
      // Anything in here is fired on component unmount.
      flushState();
    };
  }, []);

  const responsible = courseDetail?.responsible?.description
    ? `• ${courseDetail?.responsible?.description?.lastname} ${
      courseDetail?.responsible?.description?.firstname
      }`
    : "";
  const currentDate = !!onDate ? convertDateToString(onDate) : "";
  const courseTitle = `${courseDetail?.course_code ? ` • ${courseDetail?.course_code}` : ""} ${responsible} ${!!currentDate ? ` • ${currentDate}`:""}`;

  // render function
  return (
    <Layout className="main-full-content">
      <Helmet>
        <title>{t("labels.course")}</title>
        <meta name="description" content="Description of Cours" />
      </Helmet>
      <Content className="main-content-container course-detail">
        <div className="course-detail__header">
          <div
            className="go-back"
            onClick={() => history.push(goBack ? goBack : `/сours`)}
          >
            <ArrowLeftOutlined />{" "}
            {goBackTitle
              ? `${goBackTitle} ${courseTitle}`
              : `${t("labels.course")} ${courseTitle}`}
          </div>
        </div>
        <div className="course-detail__main">
          <CourseCard data={currentCourse} />
          <ExcusedStudents
            course={currentCourse}
            loading={loading}
            dataSource={currentCourse?.students || []}
            onDate={onDate}
            absences={onDateAbsences}
            attendances={currentAttendances}
          >
            <AttendanceControl
              mode="course"
              courseID={getObjId(currentCourse)}
              onDate={onDate}
              timeZone={timeZone}
              updateCourse={courseData => setCurrentCourse(courseData)}
              updateAttendances={attendanceData => {
                if (!!attendanceData) {
                  const hastAtt = currentAttendances?.find(
                    att => getObjId(att) == getObjId(attendanceData),
                  );
                  if (!!!hastAtt) {
                    setCurrentAttendances([
                      ...currentAttendances,
                      attendanceData,
                    ]);
                  } else {
                    const newAtts = currentAttendances?.map(att => {
                      if (getObjId(att) == getObjId(attendanceData)) {
                        return {
                          ...att,
                          ...attendanceData,
                        };
                      } else {
                        return att;
                      }
                    });
                    setCurrentAttendances([...newAtts]);
                  }
                }
              }}
            />
          </ExcusedStudents>
        </div>
      </Content>
    </Layout>
  );
}

CourseDetailMobile.propTypes = {
  dispatch: PropTypes.func.isRequired,
  flushState: PropTypes.func.isRequired,
  loading: PropTypes.bool,
};

const mapStateToProps = createStructuredSelector({
  loading: makeSelectLoading(),
  error: makeSelectError(),
  courseDetail: makeSelectCourseDetail(),
  onDateAbsences: makeSelectOnDateAbsences(),
  onDateAttendances: makeSelectOnDateAttendances(),
});

function mapDispatchToProps(dispatch) {
  return {
    dispatch,
    flushState: () => dispatch(flushState()),
    loadCourse: (id, onDate, timeZone) => dispatch(loadCourse(id, onDate, timeZone)),
    updateCourse: itemData => dispatch(updateCourse(itemData)),
  };
}

const withConnect = connect(
  mapStateToProps,
  mapDispatchToProps,
);

export default compose(
  withConnect,
  memo,
  withUser,
)(CourseDetailMobile);
